import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  communicationsService,
  ConversationDetail,
  ConversationSummary,
} from '../../services/communicationsService';
import { PageHeader, Button, FormField, TextInput, TextArea } from '../../components/ui';

function formatTime(iso?: string | null) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    return isToday
      ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
  } catch {
    return '';
  }
}

function titleForThread(t: ConversationSummary): string {
  if (t.subject) return t.subject;
  if (t.type === 'CLIENT' && t.client?.name) return t.client.name;
  const others = (t.participants || []).filter((p) => p.user);
  if (others.length > 0) {
    return others
      .map((p) => p.user?.name || p.emailAddress || 'Unknown')
      .slice(0, 2)
      .join(', ');
  }
  return 'Conversation';
}

const EngineerMessages: React.FC = () => {
  const qc = useQueryClient();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement | null>(null);

  const { data: threads = [] } = useQuery({
    queryKey: ['engineer', 'conversations'],
    queryFn: () => communicationsService.listConversations(),
    refetchInterval: 30_000,
  });

  const activeThread = useMemo(
    () => (threads as ConversationSummary[]).find((t) => t.id === activeId) || null,
    [threads, activeId],
  );

  const { data: detail } = useQuery<ConversationDetail>({
    queryKey: ['engineer', 'conversation', activeId],
    queryFn: () => communicationsService.getConversation(activeId as string),
    enabled: !!activeId,
    refetchInterval: 20_000,
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [detail?.messages?.length]);

  const sendMessage = async () => {
    if (!activeId || !draft.trim()) return;
    const body = draft.trim();
    setDraft('');
    await communicationsService.sendMessage(activeId, body, 'IN_APP');
    qc.invalidateQueries({ queryKey: ['engineer', 'conversation', activeId] });
    qc.invalidateQueries({ queryKey: ['engineer', 'conversations'] });
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">
      <PageHeader
        title="Messages"
        subtitle="Chat with your admin team and the customer on your active job."
        actions={
          <Button size="sm" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setNewOpen(true)}>
            New
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-[320px,1fr] gap-4 min-h-[60vh]">
        <aside className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-3 py-2 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wide">
            Threads
          </div>
          <ul className="divide-y divide-slate-100 max-h-[60vh] overflow-auto">
            {threads.length === 0 && (
              <li className="p-4 text-sm text-slate-500">No conversations yet.</li>
            )}
            {threads.map((t) => {
              const isActive = t.id === activeId;
              return (
                <li
                  key={t.id}
                  onClick={() => setActiveId(t.id)}
                  className={`px-3 py-3 cursor-pointer transition-colors ${
                    isActive ? 'bg-blue-50/60' : 'hover:bg-slate-50'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-900 truncate">
                      {titleForThread(t)}
                    </div>
                    <div className="text-[11px] text-slate-500 shrink-0">
                      {formatTime(t.lastMessageAt)}
                    </div>
                  </div>
                  <div className="text-xs text-slate-600 truncate mt-0.5">
                    {t.lastMessage?.body || 'No messages yet'}
                  </div>
                  {t.unreadCount > 0 && (
                    <div className="mt-1">
                      <span className="inline-flex items-center justify-center text-[11px] font-semibold bg-blue-600 text-white rounded-full px-1.5 py-0.5">
                        {t.unreadCount}
                      </span>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </aside>

        <section className="bg-white border border-slate-200 rounded-xl flex flex-col min-h-[60vh]">
          {!activeThread && (
            <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
              <ChatBubbleLeftRightIcon className="w-10 h-10 text-slate-300" />
              <div className="mt-2 text-sm font-semibold text-slate-700">
                Select a conversation
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Or start a new one with your admin or customer.
              </div>
            </div>
          )}

          {activeThread && (
            <>
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div>
                  <div className="text-sm font-semibold text-slate-900">
                    {titleForThread(activeThread)}
                  </div>
                  <div className="text-xs text-slate-500">
                    {activeThread.type === 'CLIENT' ? 'Customer thread' : 'Internal thread'}
                  </div>
                </div>
              </div>
              <div className="flex-1 p-4 space-y-2 overflow-auto">
                {(detail?.messages || []).map((m) => (
                  <div key={m.id} className="max-w-md">
                    <div className="text-[11px] text-slate-500 mb-0.5">
                      {m.sender?.name || m.senderName || m.senderEmail || 'System'} ·{' '}
                      {formatTime(m.createdAt)}
                    </div>
                    <div className="bg-slate-100 text-slate-900 rounded-lg px-3 py-2 text-sm whitespace-pre-wrap">
                      {m.body}
                    </div>
                  </div>
                ))}
                <div ref={endRef} />
              </div>
              <form
                className="border-t border-slate-200 p-3 flex items-end gap-2"
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMessage();
                }}
              >
                <TextArea
                  rows={2}
                  placeholder="Type a message…"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      sendMessage();
                    }
                  }}
                />
                <Button
                  type="submit"
                  disabled={!draft.trim()}
                  leftIcon={<PaperAirplaneIcon className="w-4 h-4" />}
                >
                  Send
                </Button>
              </form>
            </>
          )}
        </section>
      </div>

      {newOpen && (
        <NewEngineerConversationModal
          onClose={() => setNewOpen(false)}
          onCreated={(id) => {
            setActiveId(id);
            setNewOpen(false);
            qc.invalidateQueries({ queryKey: ['engineer', 'conversations'] });
          }}
        />
      )}
    </div>
  );
};

const NewEngineerConversationModal: React.FC<{
  onClose: () => void;
  onCreated: (id: string) => void;
}> = ({ onClose, onCreated }) => {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [participantIds, setParticipantIds] = useState<string[]>([]);
  const [clientId, setClientId] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data: contacts } = useQuery({
    queryKey: ['engineer', 'contacts'],
    queryFn: () => communicationsService.getContacts(),
  });

  const toggle = (id: string) => {
    setParticipantIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const submit = async () => {
    if (!body.trim()) {
      setError('Please write a message');
      return;
    }
    if (!participantIds.length && !clientId) {
      setError('Pick a teammate or a customer to message');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const created = await communicationsService.createConversation({
        type: clientId ? 'CLIENT' : 'INTERNAL',
        subject: subject.trim() || undefined,
        clientId: clientId || undefined,
        participantUserIds: participantIds,
        initialMessage: body.trim(),
      });
      onCreated(created.id);
    } catch (err: any) {
      setError(err?.message || 'Failed to start conversation');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <h3 className="text-sm font-semibold">New conversation</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-800">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
          <FormField label="Subject (optional)">
            <TextInput
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Parts needed for job #1234"
            />
          </FormField>
          <FormField label="Teammates">
            <div className="flex flex-wrap gap-1.5">
              {(contacts?.team || []).map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggle(t.id)}
                  className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${
                    participantIds.includes(t.id)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                  }`}
                >
                  {t.name} · {t.role}
                </button>
              ))}
              {(contacts?.team || []).length === 0 && (
                <div className="text-xs text-slate-500">No teammates available.</div>
              )}
            </div>
          </FormField>
          <FormField label="Or message a customer">
            <div className="flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setClientId('')}
                className={`text-xs rounded-full px-2.5 py-1 border ${
                  clientId === ''
                    ? 'bg-slate-900 text-white border-slate-900'
                    : 'bg-white text-slate-700 border-slate-200'
                }`}
              >
                None
              </button>
              {(contacts?.clients || []).map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setClientId(c.id === clientId ? '' : c.id)}
                  className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${
                    clientId === c.id
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-slate-400'
                  }`}
                >
                  {c.name}
                </button>
              ))}
            </div>
          </FormField>
          <FormField label="Message" required>
            <TextArea
              rows={4}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write a short message…"
            />
          </FormField>
          {error && <div className="text-xs text-red-600">{error}</div>}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} loading={submitting}>
            Start conversation
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EngineerMessages;
